import React, { useCallback, useId } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { Panel, PanelBody, PanelHead } from '../../components/primitives/Panel';
import { SavedTextarea } from '../components/SavedTextarea';
import { useReleaseOptions } from '../hooks/useReleaseOptions';
import { useAccountId } from '../../../../redux/slices/appInfo/hooks';
import type { OutreachProfileDto } from '../outreachTypes';
import type { ProfileUpdate } from '../hooks/useProfileActions';
import { profileMessages as messages } from './profileMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import styles from '../Outreach.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ProfileStoryCardProps {
  profile: OutreachProfileDto;
  onSave: (patch: ProfileUpdate) => void;
  disabled: boolean;
}

/**
 * The bio, the story of each release, the voice sample and the AI-assisted switch (design outreach
 * b1, spec §0: the pitch page shows "AI-assisted" when set). Pitches never add a fact from outside.
 */
const ProfileStoryCardComponent: React.FC<ProfileStoryCardProps> = ({ profile, onSave, disabled }) => {
  const intl = useIntl();
  const switchId = useId();
  const accountId = useAccountId() ?? 0;
  const { options } = useReleaseOptions(accountId);
  // The newest release first: the story of the song the artist pitches now.
  const release = options[0];
  const saveStory = useCallback(
    (story: string) => {
      if (release !== undefined) {
        onSave({ releaseStory: { [release.catalogItemUuid]: story } });
      }
    },
    [onSave, release]
  );

  return (
    <Panel as="section">
      <PanelHead
        title={intl.formatMessage(messages.storyTitle)}
        right={<span className={styles.srcChip}>{intl.formatMessage(messages.storyChip)}</span>}
      />
      <PanelBody>
        <div className={styles.stack}>
          <SavedTextarea
            label={intl.formatMessage(messages.bioLabel)}
            value={profile.bio}
            onSave={(bio) => onSave({ bio })}
            maxLength={1200}
            disabled={disabled}
          />
          {release !== undefined && (
            <SavedTextarea
              label={intl.formatMessage(messages.storyLabel, { song: release.title })}
              value={profile.releaseStory[release.catalogItemUuid] ?? ''}
              onSave={saveStory}
              maxLength={600}
              hint={intl.formatMessage(messages.storyHint)}
              disabled={disabled}
            />
          )}
          <SavedTextarea
            label={intl.formatMessage(messages.voiceLabel)}
            value={profile.voiceSample ?? ''}
            onSave={(voiceSample) => onSave({ voiceSample: voiceSample.trim() === '' ? null : voiceSample })}
            maxLength={2000}
            hint={intl.formatMessage(messages.voiceHint)}
            disabled={disabled}
          />
          <div className="form-check form-switch">
            <input
              id={switchId}
              type="checkbox"
              role="switch"
              className="form-check-input"
              checked={profile.aiAssisted}
              disabled={disabled}
              onChange={(event) => onSave({ aiAssisted: event.target.checked })}
            />
            <label className="form-check-label" htmlFor={switchId}>
              {intl.formatMessage(messages.aiAssisted)}
            </label>
            <p className={styles.small}>{intl.formatMessage(messages.aiAssistedHint)}</p>
          </div>
        </div>
      </PanelBody>
    </Panel>
  );
};

export const ProfileStoryCard = TypedMemo(ProfileStoryCardComponent);
